import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { useCreateLead, useKioskConfig } from '@/hooks/useRaynet'

const leadSchema = z.object({
  name: z.string().min(2, 'Jméno musí mít alespoň 2 znaky'),
  email: z.string().email('Neplatný email'),
  phone: z.string().min(9, 'Telefon musí mít alespoň 9 znaků'),
  company: z.string().optional(),
  note: z.string().optional(),
  categoryId: z.number().optional(),
})

type LeadFormData = z.infer<typeof leadSchema>

export default function KioskPage() {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const createLead = useCreateLead()
  const { data: config, isLoading: configLoading } = useKioskConfig()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<LeadFormData>({
    resolver: zodResolver(leadSchema),
  })

  const selectedCategoryId = watch('categoryId')

  // Nastavit výchozí kategorii z konfigurace
  useEffect(() => {
    if (config?.leadCategory && !selectedCategoryId) {
      setValue('categoryId', config.leadCategory)
    }
  }, [config?.leadCategory, selectedCategoryId, setValue])

  const onSubmit = async (data: LeadFormData) => {
    try {
      setSubmitError(null) // Resetujeme předchozí chyby
      console.log('🎯 Formulář odeslán s daty:', data)
      
      await createLead.mutateAsync(data)
      setIsSubmitted(true)
      reset()
      // Reset success message after 5 seconds
      setTimeout(() => setIsSubmitted(false), 5000)
    } catch (error) {
      console.error('Chyba při odesílání formuláře:', error)
      setSubmitError(
        error instanceof Error 
          ? error.message 
          : 'Došlo k neočekávané chybě. Zkuste to prosím znovu.'
      )
    }
  }

  // Zabránit odeslání formuláře při Enter v textarea
  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.altKey) {
      e.stopPropagation()
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="bg-white/10 backdrop-blur-lg border-white/20 shadow-2xl">
          <CardHeader className="text-center pb-6">
            <img 
              src="/images/bh-white-orange.png" 
              alt="Blaze Harmony" 
              className="h-12 mx-auto mb-4"
              onError={(e) => {
                // Fallback pokud se logo nenačte
                e.currentTarget.style.display = 'none'
                const fallback = document.createElement('div')
                fallback.className = 'text-2xl font-bold text-white mb-4'
                fallback.textContent = 'Blaze Harmony'
                e.currentTarget.parentNode?.insertBefore(fallback, e.currentTarget)
              }}
            />
          </CardHeader>
          
          <CardContent>
            {isSubmitted ? (
              <div className="text-center py-8">
                <div className="text-green-400 text-lg font-semibold mb-2">
                  ✓ Formulář byl úspěšně odeslán!
                </div>
                <p className="text-slate-300">
                  Děkujeme za váš zájem. Brzy se vám ozveme.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* Zobrazení chyby při odesílání */}
                {submitError && (
                  <div className="bg-red-500/20 border border-red-500/30 rounded-md p-3 backdrop-blur-sm">
                    <p className="text-red-200 text-sm">
                      ⚠️ {submitError}
                    </p>
                    <p className="text-red-300 text-xs mt-1">
                      Vaše údaje byly uloženy pro pozdější zpracování.
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-200">
                    Jméno a příjmení *
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    {...register('name')}
                    className={`bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus:border-orange-500 focus:ring-orange-500/20 ${
                      errors.name ? 'border-red-500' : ''
                    }`}
                    placeholder="Jan Novák"
                  />
                  {errors.name && (
                    <p className="text-red-400 text-sm mt-1">{errors.name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-200">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    {...register('email')}
                    className={`bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus:border-orange-500 focus:ring-orange-500/20 ${
                      errors.email ? 'border-red-500' : ''
                    }`}
                    placeholder="jan.novak@email.cz"
                  />
                  {errors.email && (
                    <p className="text-red-400 text-sm mt-1">{errors.email.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-200">
                    Telefon *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    {...register('phone')}
                    className={`bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus:border-orange-500 focus:ring-orange-500/20 ${
                      errors.phone ? 'border-red-500' : ''
                    }`}
                    placeholder="+420 123 456 789"
                  />
                  {errors.phone && (
                    <p className="text-red-400 text-sm mt-1">{errors.phone.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company" className="text-slate-200">
                    Společnost
                  </Label>
                  <Input
                    id="company"
                    type="text"
                    {...register('company')}
                    className="bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus:border-orange-500 focus:ring-orange-500/20"
                    placeholder="Název společnosti"
                  />
                </div>

                {/* Kategorie leadu */}
                {config?.availableCategories && config.availableCategories.length > 0 && (
                  <div className="space-y-2">
                    <Label htmlFor="categoryId" className="text-slate-200">
                      Kategorie leadu
                    </Label>
                    <Select
                      value={selectedCategoryId?.toString() || ''}
                      onValueChange={(value) => setValue('categoryId', value ? parseInt(value) : undefined)}
                    >
                      <SelectTrigger className={`bg-white/10 border-white/20 text-white focus:border-orange-500 focus:ring-orange-500/20 ${
                        errors.categoryId ? 'border-red-500' : ''
                      }`}>
                        <SelectValue placeholder="Vyberte kategorii" className="text-slate-400" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-600">
                        {config.availableCategories.map((category) => (
                          <SelectItem key={category.id} value={category.id.toString()}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.categoryId && (
                      <p className="text-red-400 text-sm mt-1">{errors.categoryId.message}</p>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="note" className="text-slate-200">
                    Poznámka
                  </Label>
                  <Textarea
                    id="note"
                    rows={3}
                    {...register('note')}
                    className="bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus:border-orange-500 focus:ring-orange-500/20 resize-none"
                    placeholder="Čím vám můžeme pomoci?"
                    onKeyDown={handleTextareaKeyDown}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting || createLead.isPending || configLoading}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold py-3 mt-6 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting || createLead.isPending ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Odesílám...
                    </div>
                  ) : configLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Načítám konfiguraci...
                    </div>
                  ) : (
                    'Odeslat'
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
} 